// Wire types for the GET /api/admin/projects/{id}/activity endpoint.
// Keep field names and nullability aligned with the server envelope; see
// docs/superpowers/specs/2026-05-26-admin-activity-log-design.md §6.

export type ActivityActor = {
  /** UUID of the admin user; null when the user has been hard-deleted. */
  id: string | null;
  /** Snapshot of the actor email at the time of the event. Always present. */
  email: string;
  /** Current display name from admin_users.name; null when hard-deleted. */
  name: string | null;
};

export type ActivityTarget = {
  /** "project" | "api_key" | "invite" | "team_member" | "player" | "guild" | "chat_message" */
  type: string;
  /** Heterogeneous string id (UUID, BIGINT-as-string, email); may be null. */
  id: string | null;
  /** Snapshot label (project / guild / player display name; invite email). */
  label: string | null;
};

export type ActivityItem = {
  /** Server-side BIGSERIAL row id, stringified for JS safety. */
  id: string;
  /** Canonical action string — see internal/admin/activity/actions.go. */
  action: string;
  actor: ActivityActor;
  target: ActivityTarget;
  /** Free-form per-action payload; shape depends on action. */
  details: Record<string, unknown>;
  /** RFC3339 timestamp in UTC. */
  created_at: string;
};

export type ActivityPage = {
  items: ActivityItem[];
  /** Opaque cursor for the next page; null on the last page. */
  next_cursor: string | null;
};

/**
 * Reads a numeric field out of an entry's free-form `details`.
 *
 * `details` is whatever the handler that wrote the entry chose to put there,
 * so every read is a guess that has to survive being wrong — a feed that
 * throws on one malformed row would hide every row after it.
 */
export function numericDetail(
  item: ActivityItem,
  key: string,
): number | undefined {
  const value = item.details?.[key];
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}
